import { cn, Check } from "@app/ui";
import type { I18nKey } from "../../../../lib/language.js";
import { useI18n } from "../../../../lib/useI18n.js";
import { COLOR_LABEL_KEYS, pastel, TASK_COLOR_PALETTE } from "../../../task-colors.js";
import { useHorizontalDragScroll } from "./useHorizontalDragScroll.js";

export interface ColorPick {
  color: string;
  rect: DOMRect;
}

interface Props {
  value: string | null;
  onChange: (v: string | null) => void;
  /** Fired when a NON-null color is chosen (drives the body-flood animation). */
  onPick?: (pick: ColorPick) => void;
}

const isCustom = (v: string | null) => !!v && !TASK_COLOR_PALETTE.some((c) => c.value === v);

/**
 * Custom-color picker for the sheet: a rainbow swatch plus the app palette that
 * opens the OS color picker (a real `<input type=color>` — a programmatic click
 * is blocked by some Android WebViews). Choosing a color floods the sheet body
 * (via `onPick`).
 */
export function ColorField({ value, onChange, onPick }: Props) {
  const { t } = useI18n();
  const custom = isCustom(value);
  const dragScroll = useHorizontalDragScroll();
  const { fadeStyle, ...dragScrollProps } = dragScroll;
  return (
    <div
      className="no-scrollbar -mx-3 -mt-2 flex min-h-7 cursor-grab flex-nowrap items-center gap-1 overflow-x-auto px-4 py-0.5 active:cursor-grabbing"
      style={{ touchAction: "none", scrollbarWidth: "none", ...fadeStyle }}
      {...dragScrollProps}
    >
      <label
        className={cn(
          "relative grid h-[22px] w-[22px] shrink-0 cursor-pointer place-items-center overflow-hidden rounded-full border",
          custom && "ring-2 ring-ring ring-offset-1",
        )}
        style={{ background: "conic-gradient(red, orange, yellow, lime, cyan, blue, magenta, red)" }}
        aria-label={t("color.custom")}
        onClick={(e) => {
          if (!custom) return;
          e.preventDefault();
          onChange(null);
        }}
      >
        <input
          type="color"
          value={value ?? "#3b82f6"}
          onChange={(e) => {
            onChange(e.target.value);
            onPick?.({ color: e.target.value, rect: e.currentTarget.getBoundingClientRect() });
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        {custom && <Check className="h-3 w-3 text-white drop-shadow" />}
      </label>
      {TASK_COLOR_PALETTE.filter((c) => c.value).map((c) => (
        <button
          key={c.value}
          type="button"
          aria-label={t(COLOR_LABEL_KEYS[c.label] ?? "common.color")}
          // Swatch shows the softened pastel that the block/sheet will render
          // (the stored value stays the base hex, softened consistently there).
          onClick={(e) => {
            if (value === c.value) {
              onChange(null);
              return;
            }
            onChange(c.value);
            onPick?.({ color: c.value!, rect: e.currentTarget.getBoundingClientRect() });
          }}
          className={cn("grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border", value === c.value && "ring-2 ring-ring ring-offset-1")}
          style={{ background: pastel(c.value!, 0.45) }}
        >
          {value === c.value && <Check className="h-3 w-3 text-foreground/70" />}
        </button>
      ))}
    </div>
  );
}
